import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router';
import { Toaster } from 'sonner';
import { AdminAuthProvider, useAdminAuth } from './hooks/useAdminAuth';
import AdminLayout from './components/AdminLayout';

// Pages
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import EventsPage from './pages/Events';
import SessionsPage from './pages/Sessions';
import SpeakersPage from './pages/Speakers';
import PapersPage from './pages/Papers';
import EventPartnersPage from './pages/EventPartners';
import EventCommitteePage from './pages/EventCommittee';
import AttendeesPage from './pages/Attendees';
import NotificationsPage from './pages/Notifications';
import AnalyticsPage from './pages/Analytics';
import SettingsPage from './pages/Settings';
import InviteCodesPage from './pages/InviteCodes';
import EventAppSetupPage from './pages/EventAppSetup';
import EventSessionFeedbackPage from './pages/EventSessionFeedback';
import EventInterestSettingsPage from './pages/EventInterestSettings';
import WorkspaceApplicationPage from './pages/WorkspaceApplication';
import WorkspacesPage from './pages/Workspaces';
import PlatformWorkspacesPage from './pages/PlatformWorkspaces';
import AcceptWorkspaceInvitationPage from './pages/AcceptWorkspaceInvitation';
import EventAccessPage from './pages/EventAccess';
import AIEventBuilderLandingPage from './pages/AIEventBuilderLanding';
import AIEventBuilderWorkspacePage from './pages/AIEventBuilderWorkspace';
import EventOverviewPage from './pages/EventOverview';
import EventContentHubPage from './pages/EventContentHub';
import EventPublishPage from './pages/EventPublish';
import SafetyReportsPage from './pages/SafetyReports';
import EventContentSetupPage from './pages/EventContentSetup';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAdminAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    const handler = () => navigate('/login', { replace: true });
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, [navigate]);

  if (isLoading) return (
    <div className="flex h-screen items-center justify-center bg-gray-950">
      <div className="w-5 h-5 border-2 border-gray-700 border-t-sky-500 rounded-full animate-spin" />
    </div>
  );

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function EventManagementRoute({ children }: { children: React.ReactNode }) {
  const { canAccessAdmin } = useAdminAuth();
  return canAccessAdmin ? <>{children}</> : <Navigate to="/workspace-application" replace />;
}

function EventCreationRoute({ children }: { children: React.ReactNode }) {
  const { canCreateEvents } = useAdminAuth();
  return canCreateEvents ? <>{children}</> : <Navigate to="/events" replace />;
}

function PlatformRoute({ children }: { children: React.ReactNode }) {
  const { isPlatformAdmin } = useAdminAuth();
  return isPlatformAdmin ? <>{children}</> : <Navigate to="/workspaces" replace />;
}

function LegacyExhibitorsRedirect() {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/events/${eventId}/partners`} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/workspace-application" element={<WorkspaceApplicationPage />} />
                <Route path="/workspace-invitations/accept" element={<AcceptWorkspaceInvitationPage />} />
                <Route path="/workspaces" element={<WorkspacesPage />} />
                <Route path="/platform/workspaces" element={<PlatformRoute><PlatformWorkspacesPage /></PlatformRoute>} />
                <Route path="/platform/safety" element={<PlatformRoute><SafetyReportsPage /></PlatformRoute>} />
                <Route path="/dashboard" element={<EventManagementRoute><DashboardPage /></EventManagementRoute>} />
                <Route path="/events" element={<EventManagementRoute><EventsPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/overview" element={<EventManagementRoute><EventOverviewPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/content" element={<EventManagementRoute><EventContentHubPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/content/setup" element={<EventManagementRoute><EventContentSetupPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/publish" element={<EventManagementRoute><EventPublishPage /></EventManagementRoute>} />
                <Route path="/ai-event-builder" element={<EventCreationRoute><AIEventBuilderLandingPage /></EventCreationRoute>} />
                <Route path="/ai-event-builder/new" element={<EventCreationRoute><AIEventBuilderWorkspacePage /></EventCreationRoute>} />
                <Route path="/ai-event-builder/:draftId" element={<EventManagementRoute><AIEventBuilderWorkspacePage /></EventManagementRoute>} />
                <Route path="/events/:eventId/sessions" element={<EventManagementRoute><SessionsPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/speakers" element={<EventManagementRoute><SpeakersPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/papers" element={<EventManagementRoute><PapersPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/exhibitors" element={<EventManagementRoute><LegacyExhibitorsRedirect /></EventManagementRoute>} />
                <Route path="/events/:eventId/partners" element={<EventManagementRoute><EventPartnersPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/committee" element={<EventManagementRoute><EventCommitteePage /></EventManagementRoute>} />
                <Route path="/events/:eventId/attendees" element={<EventManagementRoute><AttendeesPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/invite-codes" element={<EventManagementRoute><InviteCodesPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/app-setup" element={<EventManagementRoute><EventAppSetupPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/interests" element={<EventManagementRoute><EventInterestSettingsPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/feedback" element={<EventManagementRoute><EventSessionFeedbackPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/access" element={<EventManagementRoute><EventAccessPage /></EventManagementRoute>} />
                <Route path="/events/:eventId/safety" element={<EventManagementRoute><SafetyReportsPage /></EventManagementRoute>} />
                <Route path="/notifications" element={<EventManagementRoute><NotificationsPage /></EventManagementRoute>} />
                <Route path="/analytics" element={<EventManagementRoute><AnalyticsPage /></EventManagementRoute>} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AdminAuthProvider>
      <Router>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </Router>
    </AdminAuthProvider>
  );
}
